import { CircleAlert } from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import { Skeleton } from '../../../platform/async/Skeleton'
import type { AsyncState } from '../../../platform/async/Async'
import { Money } from '../../../platform/primitives/Money'
import { RECURRING_PATH, recurringDetailPath } from '../../recurring/recurringPaths'
import { shortDay } from '../overviewFormat'
import { OverviewWidget } from '../OverviewWidget'
import type { UpcomingView } from './upcomingView'

/**
 * Upcoming — overdue first, then what is next, at most five rows (intake A12).
 * Each amount is that bill's own `expectedCents`; there is no section total.
 * Overdue says so in words and with a ! badge, never colour alone (rule 11).
 */
export function UpcomingWidget({ state, month }: { state: AsyncState<UpcomingView>; month: string }) {
  const navigate = useNavigate()
  return (
    <OverviewWidget<UpcomingView>
      title="Upcoming"
      subtitle={month}
      href={RECURRING_PATH}
      surface="overview-upcoming"
      state={state}
      headExtra={
        (state.status === 'success' || state.status === 'stale') && state.data.overdueCount > 0 ? (
          <p className="ov-badge">
            <CircleAlert size={14} aria-hidden />
            {`${state.data.overdueCount} overdue`}
          </p>
        ) : null
      }
      skeleton={
        <>
          <Skeleton height={36} />
          <Skeleton height={36} />
          <Skeleton height={36} />
        </>
      }
      empty={{
        title: `Nothing due in ${month}`,
        action: { label: 'See recurring', onAct: () => void navigate(RECURRING_PATH) },
      }}
    >
      {({ rows }) => (
        <>
          <ul className="ov-list">
            {rows.map(({ entry, overdue }) => (
              <li key={`${entry.bill.id}:${entry.dueDate}`} className="ov-list__row">
                <span className={overdue ? 'ov-day ov-day--overdue' : 'ov-day'} aria-hidden="true">
                  {overdue ? '!' : String(Number(entry.dueDate.slice(8, 10)))}
                </span>
                <Link to={recurringDetailPath(entry.bill.id)} className="ov-list__main ov-up__main">
                  <span className="ov-list__name">{entry.bill.name}</span>
                  <span className={overdue ? 'note ov-list__meta ov-up__date ov-up__date--overdue' : 'note ov-list__meta ov-up__date'}>
                    {overdue ? `Overdue · ${shortDay(entry.dueDate)}` : shortDay(entry.dueDate)}
                    {entry.bill.isInflow ? ' · Income' : ''}
                  </span>
                </Link>
                <span className="ov-up__amount">
                  <Money cents={entry.expectedCents} currency={entry.bill.currency} kind="stock" />
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </OverviewWidget>
  )
}
